import {
  catalogFoundationShares,
  catalogGaugeShares,
  catalogPilePieceShares,
  catalogPileShares,
  catalogRoomShares,
  consolidateItems,
  resolveItemQuantity,
  splitByPurchase,
  summarizeConsolidation,
  type TakeoffQuantities,
} from '@obraplan/core/domain/takeoff/consolidation';
import {
  foundationFactorsOf,
  joinPieceFactors,
  type FoundationFactors,
} from '@obraplan/core/domain/takeoff/foundation-factors';
import {
  hasElementFactors,
  structureFactorsOf,
} from '@obraplan/core/domain/takeoff/structure-factors';
import type { CatalogService, ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { pileTotals, type PileTotals } from '@obraplan/core/domain/takeoff/pile-totals';
import type { PileType } from '@obraplan/core/domain/takeoff/pile';
import { somarPorPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import {
  hasRoomFactors,
  roomFactorsOf,
  type RoomFactors,
} from '@obraplan/core/domain/takeoff/room-quantity';
import { geometriaDaTipologia } from './medido-da-tipologia';
import { semPecasImportadas } from './pecas-importadas';
import { structureOf, type TypologyDraft, type WorkspaceDraft } from './typology-draft';

/**
 * Os números que a aba de itens diz no topo — contados uma vez, num lugar só.
 *
 * Vive em `.ts` porque é conta pura, e a faixa de resumo, a lista de serviços e a
 * consolidação precisam concordar: "44 de 46 itens lançados" na faixa e "3 de 5" numa
 * linha saem da **mesma** regra de resolver quantidade, ou a tela discorda de si mesma.
 */

/**
 * O que a tipologia decidiu e a aba que ela segue — o envelope que a consolidação lê.
 *
 * `medidos` é o medido de cada serviço nesta tipologia (Fase 24, fatia 6), vindo de
 * `medidosDaTipologia`: sem ele, o índice não tem o que multiplicar e o item calculado não
 * existe — a quantidade segue o digitado ou a planilha, como antes do índice.
 *
 * `catalog` dá a participação de cada bitola (Fase 27, RN-378): é a taxa de aço da
 * disciplina, e sem ela o item de aço do CORTE E DOBRA não reparte o medido. Na estaca a
 * bitola sai do tipo (etapa 1b): `pile` traz as estacas da tipologia, e sem ele o aço da
 * estaca não reparte.
 *
 * A quantidade pronta das peças importadas (Fase 28, RN-384) sai da própria tipologia: é a
 * soma por item que `resolveItemQuantity` junta ao calculado, e que perde para o digitado. Sai
 * daqui, e não de quem chama, para a lista, o resumo, o escopo e a consolidação da obra
 * contarem a mesma peça — e nenhum deles esquecê-la.
 *
 * `fundacao` é o fator peça a peça do bloco, do baldrame e do radier (Fase 41, RN-539), de
 * `fundacaoDaTipologia`: a medida do serviço se reparte pela medida de cada peça, como a do
 * cômodo, e cada parte leva o fator que vale nela.
 */
export function quantitiesOf(
  typology: TypologyDraft,
  medidos?: Readonly<Record<string, number>>,
  catalog?: Pick<ServiceCatalog, 'disciplines'>,
  pile?: PileTotals,
  comodos?: RoomFactors,
  fundacao?: FoundationFactors,
): TakeoffQuantities {
  const dasPecas =
    typology.pieceQuantities === undefined ? {} : somarPorPeca(typology.pieceQuantities).itens;
  return {
    overrides: typology.itemQuantities ?? {},
    ...(Object.keys(dasPecas).length === 0 ? {} : { fromPieces: dasPecas }),
    ...(typology.sheetName === undefined ? {} : { sheetName: typology.sheetName }),
    ...(medidos === undefined ? {} : { measured: medidos }),
    ...(typology.itemReasons === undefined ? {} : { reasons: typology.itemReasons }),
    ...(pile === undefined ? {} : { pile }),
    // Na fundação com taxa por peça, a bitola reparte pela taxa equivalente (Fase 41, RN-546).
    ...(catalog === undefined ? {} : { gaugeShares: catalogGaugeShares(catalog, pile, fundacao) }),
    // A parte de cada tipo de estaca na medida (Fase 39, RN-508), como a das bitolas.
    ...(catalog === undefined ? {} : { pileShares: catalogPileShares(catalog, pile) }),
    // E a de cada estaca com fator próprio (Fase 39, etapa 3, RN-511).
    ...(catalog === undefined ? {} : { pilePieceShares: catalogPilePieceShares(catalog, pile) }),
    // O fator próprio dos cômodos e a parte de cada um na medida (Fase 39, etapa 3, RN-512).
    ...(comodos === undefined ? {} : { roomFactors: comodos }),
    ...(catalog === undefined || comodos === undefined
      ? {}
      : { roomShares: catalogRoomShares(catalog, comodos) }),
    // O fator de cada peça da fundação e a parte de cada uma na medida (Fase 41, RN-539).
    ...(fundacao === undefined ? {} : { foundationFactors: fundacao }),
    ...(catalog === undefined || fundacao === undefined
      ? {}
      : { foundationShares: catalogFoundationShares(catalog, fundacao) }),
  };
}

/**
 * As peças da tipologia que repartem a medida peça a peça, com o fator de cada uma — o que
 * `quantitiesOf` recebe por último (`foundationFactors`).
 *
 * O nome ficou da Fase 41, quando eram só o bloco, o baldrame e o radier (RN-539; ADR-185): o
 * fator do tipo de bloco ou o próprio do card. Desde a Fase 42 (RN-564) entram também o pilar e
 * a viga, com o do tipo deles ou o próprio — a mesma conta, outra peça (`joinPieceFactors`) —, e
 * a escada, o muro e o pilar de contenção, só com o próprio (etapa 3, RN-571, RN-572).
 *
 * A peça da planilha por peça sai antes (F10, RN-540): o número dela já vem pronto, e não
 * reparte a medida do desenho — a mesma regra do medido. Sem nenhuma peça com fator,
 * `undefined`, e a conta é a de sempre.
 */
export function fundacaoDaTipologia(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): FoundationFactors | undefined {
  return joinPieceFactors(
    pecasDaFundacao(workspace, typology),
    estruturaDaTipologia(workspace, typology),
  );
}

/**
 * O bloco, o baldrame e o radier da tipologia com fator (Fase 41, RN-539) — e, desde a Fase 42
 * (etapa 3 — G3; RN-571, RN-572), a escada, o muro e o pilar de contenção, com o fator e a taxa
 * próprios do card: sem tipo, a mesma conta (`stairAndRetainingPieces`, dentro de
 * `foundationFactorsOf`). A importada da planilha por peça sai antes, como a da fundação (RN-574).
 */
function pecasDaFundacao(
  workspace: Pick<WorkspaceDraft, 'pileCapTypes'>,
  typology: TypologyDraft,
): FoundationFactors | undefined {
  if (
    (typology.pileCaps?.length ?? 0) === 0 &&
    (typology.groundBeams?.length ?? 0) === 0 &&
    (typology.slabs?.length ?? 0) === 0 &&
    (typology.stairs?.length ?? 0) === 0 &&
    (typology.retainingWalls?.length ?? 0) === 0 &&
    (typology.retainingColumns?.length ?? 0) === 0
  ) {
    return undefined;
  }
  const desenhadas = semPecasImportadas(typology);
  return foundationFactorsOf({
    ...(desenhadas.pileCaps === undefined ? {} : { pileCaps: desenhadas.pileCaps }),
    ...(desenhadas.groundBeams === undefined ? {} : { groundBeams: desenhadas.groundBeams }),
    ...(desenhadas.slabs === undefined ? {} : { slabs: desenhadas.slabs }),
    ...(workspace.pileCapTypes === undefined ? {} : { pileCapTypes: workspace.pileCapTypes }),
    ...(desenhadas.stairs === undefined ? {} : { stairs: desenhadas.stairs }),
    ...(desenhadas.retainingWalls === undefined
      ? {}
      : { retainingWalls: desenhadas.retainingWalls }),
    ...(desenhadas.retainingColumns === undefined
      ? {}
      : { retainingColumns: desenhadas.retainingColumns }),
  });
}

/**
 * O pilar e a viga que esta tipologia vê — os dela e os da divisa que a toca (`structureOf`) —,
 * com a medida de cada um nesta conta, o fator que vale nele (Fase 42, RN-563, RN-564) e a taxa
 * de aço que vale nele — a própria ou a do tipo (etapa 2, RN-568, RN-569), que o aço do serviço lê
 * pelo mesmo caminho da fundação (`foundationSteelRates`, `foundationShares`).
 *
 * A pergunta barata vem antes: sem nenhuma peça com fator próprio, taxa própria ou tipo que tenha
 * composição ou taxa, `undefined`, e o desenho não é refeito. Com alguma, a medida de cada peça
 * sai da geometria da tipologia inteira (`geometriaDaTipologia`), a mesma do medido do serviço — a
 * viga da divisa entra com a parte que é desta tipologia.
 */
function estruturaDaTipologia(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): FoundationFactors | undefined {
  const { elements } = structureOf(workspace, typology);
  if (elements.length === 0 || !hasElementFactors(elements, workspace.elementTypes ?? [])) {
    return undefined;
  }
  return structureFactorsOf({
    elements,
    ...(workspace.elementTypes === undefined ? {} : { elementTypes: workspace.elementTypes }),
    geometry: geometriaDaTipologia(workspace, typology).elements,
  });
}

/**
 * Os cômodos da tipologia com fator próprio, e a conta do desenho de cada um (Fase 39, RN-512) —
 * o que `quantitiesOf` recebe por último.
 *
 * A pergunta barata vem antes: sem nenhum cômodo com fator próprio, `undefined`, e o desenho não
 * é refeito. Com algum, a conta é a da tipologia inteira (`geometriaDaTipologia`), com as
 * divisas, para a parte de cada cômodo ser a mesma do medido do serviço.
 */
export function comodosDaTipologia(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): RoomFactors | undefined {
  if (!hasRoomFactors(typology.rooms)) return undefined;
  return roomFactorsOf(typology.rooms, geometriaDaTipologia(workspace, typology).rooms);
}

/**
 * As estacas da tipologia, contadas — só as posicionadas (RN-364).
 *
 * Não precisa do desenho inteiro: a estaca é ela mesma, o tipo e as marcas. É o que deixa a
 * consolidação da obra repartir o aço da estaca sem refazer a geometria de cada tipologia.
 *
 * A estaca de nome importado pela planilha por peça não conta (Fase 28, RN-384): o aço dela
 * já veio pronto, por bitola, e reparti-lo de novo pelo tipo o contaria duas vezes — a mesma
 * regra de `geometriaDaTipologia`.
 */
export function pileDaTipologia(
  typology: Pick<TypologyDraft, 'pieces' | 'pieceMarks' | 'pieceQuantities'>,
  pileTypes: readonly PileType[] | undefined,
): PileTotals | undefined {
  const pieces = semPecasImportadas(typology).pieces ?? [];
  if (pieces.length === 0) return undefined;
  return pileTotals(pieces, pileTypes ?? [], typology.pieceMarks ?? []);
}

/** Quantos itens deste serviço têm quantidade — lançada ou herdada da planilha. */
export function lancadosDoServico(servico: CatalogService, quantities: TakeoffQuantities): number {
  return servico.items.filter(
    (item) => resolveItemQuantity(item, servico.key, quantities).quantity !== undefined,
  ).length;
}

export interface ResumoDosItens {
  /** Serviços cadastrados na obra — os dela e os globais. */
  readonly servicos: number;
  /** Os que têm ao menos um item: é neles que há o que lançar. */
  readonly comMaterial: number;
  readonly itens: number;
  readonly lancados: number;
  /** Materiais distintos com quantidade — a lista de compra desta tipologia. */
  readonly materiais: number;
  readonly comCodigo: number;
  readonly compartilhados: number;
  readonly funcoes: number;
  readonly horas: number;
}

export function resumoDosItens(
  catalog: ServiceCatalog,
  draft: TypologyDraft,
  medidos?: Readonly<Record<string, number>>,
  pile?: PileTotals,
  comodos?: RoomFactors,
  fundacao?: FoundationFactors,
): ResumoDosItens {
  const quantities = quantitiesOf(draft, medidos, catalog, pile, comodos, fundacao);
  let servicos = 0;
  let comMaterial = 0;
  let itens = 0;
  let lancados = 0;
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      servicos += 1;
      if (servico.items.length === 0) continue;
      comMaterial += 1;
      itens += servico.items.length;
      lancados += lancadosDoServico(servico, quantities);
    }
  }

  const { materials, labor } = splitByPurchase(consolidateItems(catalog, quantities));
  const resumo = summarizeConsolidation(materials);

  return {
    servicos,
    comMaterial,
    itens,
    lancados,
    materiais: resumo.items,
    comCodigo: resumo.withCode,
    compartilhados: resumo.shared,
    funcoes: labor.length,
    horas: labor.reduce((total, item) => total + item.total, 0),
  };
}
